/**
 * Home metric period — plan/50.
 * Per widget: presets 7d · 30d · MTD on card; Custom from→to in Show more.
 */

export type HomePeriodPreset = "7" | "30" | "mtd" | "custom";

export type HomePeriodState = {
  preset: HomePeriodPreset;
  /** Inclusive YMD — used when preset === custom */
  from?: string;
  to?: string;
};

export const HOME_PERIOD_CUSTOM_MAX_DAYS = 92;

const YMD_RE = /^\d{4}-\d{2}-\d{2}$/;

export function isYmd(value: string): boolean {
  if (!YMD_RE.test(value)) return false;
  const [ys, ms, ds] = value.split("-");
  const y = Number(ys);
  const m = Number(ms);
  const d = Number(ds);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return (
    dt.getUTCFullYear() === y &&
    dt.getUTCMonth() === m - 1 &&
    dt.getUTCDate() === d
  );
}

export function normalizeHomePeriodState(
  raw: Partial<HomePeriodState> | null | undefined,
): HomePeriodState {
  const preset = raw?.preset;
  if (preset === "30" || preset === "mtd" || preset === "custom" || preset === "7") {
    if (preset === "custom") {
      const from = typeof raw?.from === "string" ? raw.from : "";
      const to = typeof raw?.to === "string" ? raw.to : "";
      if (isYmd(from) && isYmd(to) && from <= to) {
        return { preset: "custom", from, to };
      }
      return { preset: "7" };
    }
    return { preset };
  }
  // Legacy: bare "7" / "30" string from earlier localStorage
  if (raw === ("30" as unknown) || (raw as { preset?: string })?.preset === "30") {
    return { preset: "30" };
  }
  return { preset: "7" };
}

/** Migrate prior top-products / sales-trend period keys (bare "7"|"30"). */
export function migrateLegacyPeriodDays(raw: string | null): HomePeriodState {
  if (raw === "30") return { preset: "30" };
  if (raw === "7") return { preset: "7" };
  try {
    if (raw?.startsWith("{")) {
      return normalizeHomePeriodState(JSON.parse(raw) as Partial<HomePeriodState>);
    }
  } catch {
    /* ignore */
  }
  return { preset: "7" };
}

export function homePeriodLabel(
  state: HomePeriodState,
  kind: "sales" | "products",
): string {
  const suffix =
    kind === "sales"
      ? "tax-incl POS + delivered SO − returns"
      : "by sales";
  if (state.preset === "custom" && state.from && state.to) {
    return `${state.from} → ${state.to} · ${suffix}`;
  }
  if (state.preset === "30") return `Last 30 days · ${suffix}`;
  if (state.preset === "mtd") return `Month to date · ${suffix}`;
  return `Last 7 days · ${suffix}`;
}

export function homePeriodEmptyMessage(
  state: HomePeriodState,
  kind: "sales" | "products",
): string {
  if (kind === "sales") return "No sales in this period.";
  if (state.preset === "custom" && state.from && state.to) {
    return `No product sales from ${state.from} to ${state.to}.`;
  }
  if (state.preset === "30") return "No product sales in the last 30 days.";
  if (state.preset === "mtd") return "No product sales month to date.";
  return "No product sales in the last 7 days.";
}

export function homePeriodPresetButtonLabel(preset: HomePeriodPreset): string {
  if (preset === "mtd") return "MTD";
  if (preset === "custom") return "Custom";
  return `${preset}d`;
}

function periodKey(widget: "salesTrend" | "topProducts", companySlug: string) {
  return `icraftx.homePeriod.v1:${widget}:${companySlug}`;
}

function legacyKey(widget: "salesTrend" | "topProducts", companySlug: string) {
  return widget === "salesTrend"
    ? `icraftx.salesTrendPeriod.v1:${companySlug}`
    : `icraftx.topProductsPeriod.v1:${companySlug}`;
}

export function loadHomePeriod(
  widget: "salesTrend" | "topProducts",
  companySlug: string,
): HomePeriodState {
  if (typeof localStorage === "undefined" || !companySlug) {
    return { preset: "7" };
  }
  try {
    const raw = localStorage.getItem(periodKey(widget, companySlug));
    if (raw) return migrateLegacyPeriodDays(raw);
    const legacy = localStorage.getItem(legacyKey(widget, companySlug));
    if (legacy) return migrateLegacyPeriodDays(legacy);
  } catch {
    /* ignore */
  }
  return { preset: "7" };
}

export function saveHomePeriod(
  widget: "salesTrend" | "topProducts",
  companySlug: string,
  state: HomePeriodState,
): void {
  if (typeof localStorage === "undefined" || !companySlug) return;
  try {
    localStorage.setItem(
      periodKey(widget, companySlug),
      JSON.stringify(normalizeHomePeriodState(state)),
    );
  } catch {
    /* quota / private mode */
  }
}

/** Days between from→to inclusive; 0 if invalid. */
export function homePeriodDayCount(from: string, to: string): number {
  if (!isYmd(from) || !isYmd(to) || from > to) return 0;
  const a = Date.UTC(
    Number(from.slice(0, 4)),
    Number(from.slice(5, 7)) - 1,
    Number(from.slice(8, 10)),
  );
  const b = Date.UTC(
    Number(to.slice(0, 4)),
    Number(to.slice(5, 7)) - 1,
    Number(to.slice(8, 10)),
  );
  return Math.floor((b - a) / 86_400_000) + 1;
}
